<template>
  <div class="ch-page bg-light-page">

    <div class="ch-scroll" ref="scrollContainer" @scroll="onScroll">

      <ConciergeHero :hero-style="heroStyle" :reservation="reservation || { greeting: '' }" />

      <div class="px-3 ch-reservation-wrap">
        <div v-if="reservationError" class="ch-error-card px-3 py-3">
          <p class="mb-0" style="font-size:13px;color:#C04040">{{ reservationError }}</p>
        </div>
        <ConciergeReservationCard v-else-if="reservation" v-animate-in="animPhase >= 1" :reservation="reservation" @view-reservation="viewReservation" />
      </div>

      <div class="px-3 ch-quick-services-wrap">
        <ConciergeQuickMenu
          v-animate-in="animPhase >= 2"
          :items="quickServiceItems"
          :dnd="dnd"
          :reservation-status="reservation && reservation.status || ''"
          @select="onQuickServiceClick"
          @toggle-dnd="toggleDnd"
          @show-toast="showToast"
        />
      </div>

      <ConciergeEssentialsSection v-animate-in="animPhase >= 3" :wifi="reservation && reservation.wifiCredential || null" :facts="reservationFacts" @toast="showToast" />

      <div v-if="isLoadingServiceList">
        <div class="px-3 mb-4">
          <div class="ch-skel ch-skel-title mb-3"></div>
          <div class="ch-skel ch-skel-banner radius-16 mb-3"></div>
          <div class="d-flex ch-skel-row">
            <div class="ch-skel ch-skel-card-sm radius-16 flex-shrink-0"></div>
            <div class="ch-skel ch-skel-card-sm radius-16 flex-shrink-0"></div>
          </div>
        </div>
        <div class="px-3 mb-4">
          <div class="ch-skel ch-skel-title mb-3" style="width:55%"></div>
          <div class="ch-skel ch-skel-banner radius-16 mb-3"></div>
          <div class="d-flex ch-skel-row">
            <div class="ch-skel ch-skel-card-lg radius-16 flex-shrink-0"></div>
            <div class="ch-skel ch-skel-card-lg radius-16 flex-shrink-0"></div>
          </div>
        </div>
      </div>

      <template v-if="!isLoadingServiceList">
        <ConciergeDiningSection
          v-for="svc in diningServices"
          :key="svc.id"
          :service="svc"
          v-animate-in="animPhase >= 1"
          @navigate="navigateTo"
        />

        <ConciergeActivitiesSection
          v-for="svc in activityServices"
          :key="svc.id"
          :service="svc"
          v-animate-in="animPhase >= 1"
          @navigate="navigateTo"
        />

        <ConciergeVoucherSection
          v-if="voucherServices.length > 0"
          :items="voucherServices"
          v-animate-in="animPhase >= 1"
          @navigate="navigateTo"
        />
      </template>

      <ConciergeFeedbackSection
        v-if="reservation && reservation.status !== 'NEW_RESERVATION'"
        v-animate-in="animPhase >= 1"
        :feedback-result="feedbackResult"
        :is-loading="isLoadingFeedback"
        @navigate="navigateTo"
      />

      <div style="height:80px"></div>
    </div>

    <ConciergeBottomNav
      :active-chat="chatSheet"
      :active-reservation="reservationSheet"
      @chat="chatSheet = true"
      @reservation="reservationSheet = true"
    />

    <transition name="ch-fade">
      <div v-if="toast" class="ch-toast">{{ toast }}</div>
    </transition>

    <ConciergeChatSheet
      v-if="chatSheet"
      @close="chatSheet = false"
      @email="openChatEmail"
      @whatsapp="openWhatsApp"
    />

    <ConciergeReservationSheet
      v-if="reservationSheet && reservation"
      :reservation="reservation"
      :room-type="roomType"
      :merchant="getMerchant"
      @close="reservationSheet = false"
      @pay="showToast('Redirecting to payment...')"
    />

    <transition name="ch-splash">
      <div v-if="isLoadingReservation" class="ch-splash" :style="heroStyle">
        <div class="ch-splash-overlay"></div>
        <div class="ch-splash-body">
          <div class="ch-spinner-ring"></div>
          <p class="ch-splash-label">Preparing your experience</p>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
  import { mapGetters, mapMutations } from 'vuex'
  import { get, put } from '@/services/index.js'
  import { formatDate } from '@/utils/index.js'
  import moment from 'moment'
  import {
    MessageCircle,
    Moon,
    Home,
    AlertTriangle,
    Coffee,
    Compass,
    Heart,
    Star,
    Ticket,
    Layers,
  } from 'lucide-vue'
  import ConciergeBottomNav    from '@/components/v2/ConciergeBottomNav'
  import ConciergeQuickMenu    from '@/components/hotel/concierge/general/ConciergeQuickMenu'
  import ConciergeHero             from '@/components/v2/ConciergeHero'
  import ConciergeReservationCard  from '@/components/v2/ConciergeReservationCard'
  import ConciergeChipNav          from '@/components/v2/ConciergeChipNav'
  import ConciergeEssentialsSection from '@/components/v2/ConciergeEssentialsSection'
  import ConciergeDiningSection     from '@/components/v2/ConciergeDiningSection'
  import ConciergeActivitiesSection from '@/components/v2/ConciergeActivitiesSection'
  import ConciergeVoucherSection    from '@/components/v2/ConciergeVoucherSection'
  import ConciergeFeedbackSection   from '@/components/v2/ConciergeFeedbackSection'
  import ConciergeChatSheet        from '@/components/v2/ConciergeChatSheet'
  import ConciergeReservationSheet from '@/components/v2/ConciergeReservationSheet'

  function startAnimateObserver(el) {
    var root = el.closest('.ch-scroll') || null
    var observer = new IntersectionObserver(function(entries) {
      if (entries[0].isIntersecting) {
        el.classList.remove('animate-hidden')
        el.classList.add('animate-visible')
        observer.disconnect()
      }
    }, { root: root, threshold: 0.12 })
    observer.observe(el)
    el._animateObserver = observer
  }

  var ALL_QUICK_SERVICES = [
    { id: 'chat',         label: 'Chat',           icon: MessageCircle, route: 'ConciergeChat' },
    { id: 'dnd',          label: 'Do Not Disturb', icon: Moon },
    { id: 'housekeeping', label: 'Housekeeping',   icon: Home,          route: 'ConciergeHousekeeping' },
    { id: 'report-issue', label: 'Report Issue',   icon: AlertTriangle, route: 'ConciergeReportIssue' },
    { id: 'dining',       label: 'Dining',         icon: Coffee,        section: 'dining' },
    { id: 'activity',     label: 'Activities',     icon: Compass,       section: 'activities' },
    { id: 'voucher',      label: 'Voucher',         icon: Ticket,        route: 'ConciergeThingsToDo' },
    { id: 'tip',          label: 'Tip',            icon: Heart,         route: 'ConciergeTip' },
    { id: 'feedback',     label: 'Feedback',       icon: Star,          route: 'ConciergeFeedback' },
  ]

  const NAV_SECTIONS = [
    { id: 'essentials', label: 'Essentials' },
    { id: 'dining',     label: 'Dining' },
    { id: 'activities', label: 'Experiences' },
    { id: 'feedback',   label: 'Feedback' },
  ]

  export default {
    name: 'ConciergeHome',

    components: {
      ConciergeBottomNav,
      ConciergeQuickMenu,
      ConciergeHero,
      ConciergeReservationCard,
      ConciergeChipNav,
      ConciergeEssentialsSection,
      ConciergeDiningSection,
      ConciergeActivitiesSection,
      ConciergeVoucherSection,
      ConciergeFeedbackSection,
      ConciergeChatSheet,
      ConciergeReservationSheet,
    },

    directives: {
      animateIn: {
        inserted: function(el, binding) {
          el.classList.add('animate-hidden')
          if (binding.value) startAnimateObserver(el)
        },
        update: function(el, binding) {
          if (binding.value && !binding.oldValue) startAnimateObserver(el)
        },
        unbind: function(el) {
          if (el._animateObserver) {
            el._animateObserver.disconnect()
            el._animateObserver = null
          }
        },
      },
    },

    metaInfo() {
      return {
        title: 'Concierge',
        titleTemplate: this.getMetaTitleTemplate,
      }
    },

    data() {
      return {
        NAV_SECTIONS,

        reservation:            null,
        reservationFacts:       [],
        roomType:               null,
        isLoadingReservation:   true,
        reservationError:       '',
        animPhase:              0,

        serviceList:            [],
        isLoadingServiceList:   true,

        feedbackResult:         null,
        isLoadingFeedback:      false,

        activeSection:     'requests',
        chatSheet:         false,
        reservationSheet:  false,
        dnd:               false,
        toast:             null,
      }
    },

    computed: {
      ...mapGetters(['getMerchant', 'getMetaTitleTemplate', 'getConciergeHomeAuth']),

      diningServices() {
        return this.serviceList.filter(function(s) {
          return s.service_type === 'MERCHANT_POS' &&
            (s.merchant_pos && (s.merchant_pos.pos_type === 'ROOM_DINING' || s.merchant_pos.pos_type === 'RESTAURANT'))
        })
      },

      activityServices() {
        return this.serviceList.filter(function(s) {
          return s.service_type === 'MERCHANT_POS' &&
            (s.merchant_pos && (s.merchant_pos.pos_type === 'ACTIVITY' || s.merchant_pos.pos_type === 'SPA'))
        })
      },

      voucherServices() {
        return this.serviceList.filter(function(s) {
          return s.service_type === 'UPSELLING_PRODUCT_GROUP'
        })
      },

      quickServiceItems() {
        var byId = {}
        ALL_QUICK_SERVICES.forEach(function(s) { byId[s.id] = s })
        var ids = ['chat', 'dnd', 'housekeeping', 'report-issue']
        if (this.diningServices.length > 0)   ids.push('dining')
        if (this.activityServices.length > 0) ids.push('activity')
        if (this.voucherServices.length > 0)  ids.push('voucher')
        ids.push('tip', 'feedback')
        return ids.map(function(id) { return byId[id] })
      },

      heroStyle() {
        const merchant = this.getMerchant
        const img = (merchant && merchant.merchant_images && merchant.merchant_images[0] && merchant.merchant_images[0].image_url) || ''
        return {
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0.85) 100%), url('${img}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      },
    },

    watch: {
      isLoadingReservation: function(val) {
        if (!val) {
          setTimeout(() => { this.animPhase = 1 }, 600)
          setTimeout(() => { this.animPhase = 2 }, 750)
          setTimeout(() => { this.animPhase = 3 }, 900)
        }
      },
    },

    created() {
      this._toastTimer = null
      this._scrollRaf  = null
    },

    mounted() {
      const open  = this.$route.query.open
      const toast = this.$route.query.toast
      if (open === 'chat') {
        this.chatSheet = true
      } else if (open === 'reservation') {
        this.reservationSheet = true
      }
      if (open || toast) {
        this.$router.replace({ query: {} })
      }
      if (toast) {
        this.$nextTick(() => { this.showToast(toast) })
      }
      this.fetchReservation()
      this.fetchServiceList()
    },

    beforeDestroy() {
      clearTimeout(this._toastTimer)
      cancelAnimationFrame(this._scrollRaf)
    },

    methods: {
      ...mapMutations(['setConciergeHomeAuth', 'resetConciergeHomeAuth', 'setConciergeReservation']),

      fetchFeedbackResult() {
        var auth = this.getConciergeHomeAuth
        var reservationId = auth.pms_detail_id
        if (!reservationId) return
        this.isLoadingFeedback = true
        get('CONCIERGE', 'reservation-feedback-result/' + reservationId, {
          merchant_id: this.getMerchant.id,
        }, { type: 'concierge_token_direct', token: auth.token }).then(function(response) {
          var res = response.data
          if (res.success) this.feedbackResult = res.data || null
          this.isLoadingFeedback = false
        }.bind(this)).catch(function() {
          this.isLoadingFeedback = false
        }.bind(this))
      },

      fetchServiceList() {
        get('CORE_HOTEL', 'merchant-service', {
          merchant_id: this.getMerchant.id,
          page_type: 'BASE_HOME_BOOK_SERVICE',
        }).then(function(response) {
          var res = response.data
          if (res.success) this.serviceList = res.data || []
          this.isLoadingServiceList = false
        }.bind(this)).catch(function() {
          this.isLoadingServiceList = false
        }.bind(this))
      },

      fetchReservation() {
        var queryId    = this.$route.query.reservation_id
        var queryToken = this.$route.query.token
        var reservationId, token

        if (queryId && queryToken) {
          reservationId = queryId
          token         = queryToken
          this.setConciergeHomeAuth([
            { key: 'pms_detail_id', value: reservationId },
            { key: 'token',         value: token }
          ])
          this.$router.replace({ query: {} })
        } else {
          var auth = this.getConciergeHomeAuth
          if (auth.pms_detail_id && auth.token) {
            reservationId = auth.pms_detail_id
            token         = auth.token
          } else {
            this.reservationError     = 'Invalid concierge link.'
            this.isLoadingReservation = false
            return
          }
        }

        get('CONCIERGE', 'reservation/' + reservationId, {
          merchant_id: this.getMerchant.id
        }, { type:'concierge_token_direct', token}).then(response => {
          var res = response.data
          if (res.success) {
            var d = res.data
            var daysIn = Math.max(1, Math.min(
              moment().diff(moment(d.arrival_date), 'days') + 1,
              d.total_night
            ))
            this.setConciergeHomeAuth([
              { key: 'reservation_id',     value: d.id || '' },
              { key: 'folio_number',       value: d.folio_number || '' },
              { key: 'reservation_status', value: d.status || '' },
            ])
            this.setConciergeReservation({
              guest_profile: d.guest_profile || {
                name:  ((d.booked_by && d.booked_by.name)  || ''),
                email: ((d.booked_by && d.booked_by.email) || ''),
                phone: ((d.booked_by && d.booked_by.phone) || '')
              },
              room: d.room || {},
              id:   d.id   || ''
            })
            this.dnd = Boolean(d.is_do_not_disturb)
            var wifiCred = d.room && d.room.wifi_credential
            this.reservation = {
              guestName:      ((d.booked_by.prefix_name || '') + ' ' + (d.booked_by.name || '') + ' ' + (d.booked_by.last_name || '')).trim(),
              greeting:       d.booked_by.name || '',
              bookingId:      d.booking_number,
              room:           (d.room_type && d.room_type.name) ? d.room_type.name : '',
              roomNumber:     (d.room && d.room.name) ? d.room.name : '-',
              floor:          '',
              guests:         (Number(d.adult) || 0) + (Number(d.child) || 0),
              nights:         d.total_night,
              checkIn:        formatDate(d.arrival_date),
              checkOut:       formatDate(d.departure_date),
              status:         d.status,
              daysIn:         daysIn,
              detailId:       d.pms_detail_id || '',
              folio:          d.folio_number || '',
              wifiCredential: wifiCred ? { network: wifiCred.ssid, password: wifiCred.password } : null,
            }

            const beds = (d.room_type && d.room_type.bed) || []
            const bedValue = beds.map(function(b) { return b.qty + ' ' + b.name }).join(' · ')

            const rate = (d.rate && d.rate[0]) || null
            const breakfastValue = (rate && rate.is_included_breakfast) ? 'Included' : 'Not included'

            const allFacilities = (d.room_type && d.room_type.facility) || []
            const highlightFacilities = allFacilities.filter(function(f) { return f.is_highlight })
            const shownFacilities = highlightFacilities.length ? highlightFacilities : allFacilities.slice(0, 3)
            const facilitiesValue = shownFacilities.map(function(f) { return f.name }).join(' · ')

            const facts = []
            if (rate) facts.push({ icon: Coffee, label: 'Breakfast', value: breakfastValue })
            if (bedValue) facts.push({ icon: Layers, label: 'Bed type', value: bedValue })
            if (facilitiesValue) facts.push({ icon: Star, label: 'Facilities', value: facilitiesValue })
            this.reservationFacts = facts

            this.roomType = d.room_type || null
            if (d.feedback_result) this.fetchFeedbackResult()
          } else {
            this.reservationError = res.message || 'Failed to load reservation.'
          }
          this.isLoadingReservation = false
        }).catch(() => {
          this.reservationError     = 'Failed to load reservation.'
          this.isLoadingReservation = false
        })
      },

      viewReservation() {
        this.reservationSheet = true
      },

      navigateTo(route) {
        if (typeof route === 'string') this.$router.push({ name: route })
        else this.$router.push(route)
      },

      onQuickServiceClick(item) {
        if (item.id === 'chat') { this.chatSheet = true; return }
        if (item.id === 'feedback' && this.feedbackResult) {
          this.scrollToSection('feedback')
          return
        }
        if (item.route)        this.navigateTo(item.route)
        else if (item.section) this.scrollToSection(item.section)
      },

      openChatEmail() {
        window.location.href = 'mailto:concierge@hotel.com'
        this.chatSheet = false
      },

      openWhatsApp() {
        window.open('https://wa.me/628123456789', '_blank')
        this.chatSheet = false
      },

      toggleDnd() {
        var auth = this.getConciergeHomeAuth
        var reservationId = auth.reservation_id
        if (!reservationId) return
        var next = !this.dnd
        this.dnd = next
        put('CONCIERGE', 'reservation-do-not-disturb/' + reservationId, {
          merchant_id: this.getMerchant.id,
          is_do_not_disturb: next,
        }, { type: 'concierge_token_direct', token: auth.token }).then(function(response) {
          var res = response.data
          if (res && res.success) {
            this.showToast(next ? 'Do Not Disturb activated' : 'Do Not Disturb deactivated')
          } else {
            this.dnd = !next
            this.showToast('Failed to update Do Not Disturb')
          }
        }.bind(this)).catch(function() {
          this.dnd = !next
          this.showToast('Failed to update Do Not Disturb')
        }.bind(this))
      },

      scrollToSection(id) {
        const sc = this.$refs.scrollContainer
        const el = sc ? sc.querySelector(`#sec-${id}`) : null
        if (!el || !sc) return
        sc.scrollTo({ top: el.offsetTop, behavior: 'smooth' })
      },

      onScroll() {
        if (this._scrollRaf) return
        this._scrollRaf = requestAnimationFrame(() => {
          this._scrollRaf = null
          const sc = this.$refs.scrollContainer
          if (!sc) return
          const y = sc.scrollTop + 16
          let current = NAV_SECTIONS[0].id
          for (const s of NAV_SECTIONS) {
            const el = sc.querySelector(`#sec-${s.id}`)
            if (el && el.offsetTop <= y) current = s.id
          }
          if (this.activeSection !== current) this.activeSection = current
        })
      },

      showToast(msg) {
        clearTimeout(this._toastTimer)
        this.toast = msg
        this._toastTimer = setTimeout(() => { this.toast = null }, 1800)
      },
    },
  }
</script>

<style scoped>
.ch-page {
  min-height: 100vh;
  position: relative;
}

.ch-scroll {
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.ch-reservation-wrap {
  margin-top: -46px;
  position: relative;
  z-index: 2;
  padding-bottom: 24px;
}

.ch-quick-services-wrap {
  position: relative;
  z-index: 2;
  padding-bottom: 16px;
}

.ch-error-card {
  border-radius: 16px;
  background: #FFF5F5;
  border: 1px solid #FCA5A5;
}

/* Splash loading screen */
.ch-splash {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-size: cover;
  background-position: center;
}

.ch-splash-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.5) 100%);
  backdrop-filter: blur(3px);
}

.ch-splash-body {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.ch-spinner-ring {
  width: 52px;
  height: 52px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  animation: chSpin 0.75s linear infinite;
}

@keyframes chSpin {
  to { transform: rotate(360deg); }
}

.ch-splash-label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.6px;
  margin: 0;
  text-align: center;
}

.ch-splash-enter-active { transition: opacity 0.2s ease-out; }
.ch-splash-leave-active { transition: opacity 0.55s ease-in-out; }
.ch-splash-enter, .ch-splash-leave-to { opacity: 0; }

.ch-toast {
  position: fixed;
  bottom: 90px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(30,42,51,0.92);
  backdrop-filter: blur(8px);
  color: #fff;
  padding: 11px 18px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  z-index: 1060;
  box-shadow: 0 8px 24px rgba(0,0,0,0.18);
  white-space: nowrap;
  pointer-events: none;
}

.ch-fade-enter-active, .ch-fade-leave-active { transition: opacity .25s; }
.ch-fade-enter, .ch-fade-leave-to { opacity: 0; }

.animate-hidden {
  opacity: 0;
  transform: translateY(30px);
}

.animate-visible {
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.55s ease-out, transform 0.55s cubic-bezier(0.34, 1.2, 0.64, 1);
}

.ch-skel {
  background: #E8EDF2;
  animation: chSkeletonPulse 1.5s ease-in-out infinite;
}

@keyframes chSkeletonPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.ch-skel-title {
  height: 20px;
  width: 40%;
  border-radius: 6px;
}

.ch-skel-banner {
  height: 100px;
  width: 100%;
}

.ch-skel-row {
  gap: 12px;
  overflow: hidden;
}

.ch-skel-card-sm {
  width: 148px;
  height: 175px;
}

.ch-skel-card-lg {
  width: 220px;
  height: 200px;
}
</style>



// WEBPACK FOOTER //
// src/pages/hotel/concierge/ConciergeHome.vue